/**
 * Case-insensitive exact-vs-related split over a flat term index, for the search bar's
 * command palette. Same substring approach as lib/dataCatalog/filterFeatures.js (deliberately —
 * see IMPLEMENTATION_CHECKLIST.md's reusability note), just split into two buckets instead of one
 * filtered list, and capped.
 */

export const DEFAULT_RELATED_CAP = 4;

/**
 * `exactMatches` (the prominently-shown bucket) does word-boundary PREFIX matching -- checking
 * the whole display string AND each individual word -- at every fragment length, so "vo" or
 * "vote" both promote "Local Voter Score". `mode` (useCommandPaletteMatches) only changes the
 * caller's own UI labeling/cap size now, not this matching algorithm.
 */
export function matchTerms(fragment, termIndex, { relatedCap = DEFAULT_RELATED_CAP } = {}) {
  const trimmed = (fragment || "").trim();
  if (!trimmed || !termIndex?.length) {
    return { exactMatches: [], relatedMatches: [] };
  }

  const lower = trimmed.toLowerCase();
  const isPrimaryMatch = (displayLower) =>
    displayLower.startsWith(lower) || displayLower.split(/\s+/).some((word) => word.startsWith(lower));
  const exactMatches = [];
  const relatedMatches = [];

  for (const term of termIndex) {
    const displayLower = (term.display || "").toLowerCase();
    if (!displayLower) continue;

    if (isPrimaryMatch(displayLower)) {
      exactMatches.push(term);
    } else if (relatedMatches.length < relatedCap && displayLower.includes(lower)) {
      relatedMatches.push(term);
    }
  }

  return { exactMatches, relatedMatches };
}

/**
 * Terms from a sourceType not already represented in the query, diversified across the
 * remaining sourceTypes (round-robin, not the first N of whichever sorts first) -- the fallback
 * for ADD TO YOUR SEARCH when a token has no same-category siblings of its own (a place, the
 * atomic Age term, an unresolved template pill). After picking a place, suggest a metric/
 * demographic next, not nothing.
 */
export function suggestComplementaryTerms(termIndex, usedSourceTypes, cap) {
  const groups = new Map();
  for (const term of termIndex) {
    if (usedSourceTypes.has(term.sourceType)) continue;
    if (!groups.has(term.sourceType)) groups.set(term.sourceType, []);
    groups.get(term.sourceType).push(term);
  }
  const buckets = [...groups.values()];

  const suggestions = [];
  let round = 0;
  let addedThisRound = true;
  while (suggestions.length < cap && addedThisRound) {
    addedThisRound = false;
    for (const bucket of buckets) {
      if (suggestions.length >= cap) break;
      const term = bucket[round];
      if (term) {
        suggestions.push(term);
        addedThisRound = true;
      }
    }
    round++;
  }
  return suggestions;
}
